<template>
  <div class="page-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">扫码入库</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div class="scan-panel">
        <div class="supplier-bar">
          <div class="supplier-left">
            <div class="supplier-label">当前供应商</div>
            <div class="supplier-value">{{ selectedSupplier?.supplierName || '未选择' }}</div>
          </div>
          <button class="mini-btn outline" type="button" @click="openSupplierModal">选择供应商</button>
        </div>

        <div class="supplier-bar">
          <div class="supplier-left">
            <div class="supplier-label">当前仓库</div>
            <div class="supplier-value">{{ selectedWare?.wareName || '未选择' }}</div>
          </div>
          <button class="mini-btn outline" type="button" @click="openWareModal">选择仓库</button>
        </div>

        <div class="scan-input-wrap">
          <input
            ref="scanInput"
            v-model="scanText"
            class="scan-input"
            type="text"
            placeholder="请扫码..."
            autocomplete="off"
            @input="handleScanInput"
            @keydown.enter.prevent="handleScanEnter"
          />
          <button
            v-if="scanText"
            class="input-clear"
            type="button"
            @click="clearScanText"
            aria-label="清除"
          >
            ×
          </button>
        </div>

        <div v-if="scannedList.length === 0" class="empty-state">
          <img src="@/assets/无数据.svg" alt="暂无数据" class="empty-img" />
          <p class="empty-text">~ 暂无数据 ~</p>
        </div>

        <div v-else class="card-list">
          <div class="data-card" v-for="(item, idx) in scannedList" :key="item.id || idx">
            <div class="row-title mono">{{ item.materialNo || '-' }}</div>
            <div class="row-sub">
              <span>{{ item.materialDesc || item.materialModel || '-' }}</span>
            </div>
            <div class="row-sub">
              <span>数量：{{ item.qty ?? '-' }} {{ item.packageUnitName || '' }}</span>
              <span v-if="item.batchNo" class="split">|</span>
              <span v-if="item.batchNo">批次：{{ item.batchNo }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="submit-section" v-if="scannedList.length > 0">
        <div class="summary-info">
          <span class="summary-label">已扫码：</span>
          <span class="summary-value">{{ scannedList.length }} 项</span>
        </div>
        <button class="submit-btn" type="button" @click="confirmStockIn">确认入库</button>
      </div>
    </main>

    <div v-if="supplierModalVisible" class="modal-overlay" @click.self="closeSupplierModal">
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-title">选择供应商</div>
          <button class="modal-close" @click="closeSupplierModal" aria-label="关闭">×</button>
        </div>

        <div class="modal-body">
          <div class="modal-search">
            <input
              ref="supplierKeywordInput"
              v-model="keyword"
              class="search-input"
              placeholder="请输入供应商名称搜索"
              @input="handleSearchInput"
            />
            <button
              v-if="keyword"
              class="input-clear"
              type="button"
              @click="clearKeyword"
              aria-label="清除搜索"
            >
              ×
            </button>
          </div>

          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p class="loading-text">加载中...</p>
          </div>

          <div v-else>
            <div v-if="list.length === 0" class="dropdown-empty">暂无数据</div>
            <div v-else class="modal-list">
              <button
                v-for="s in list"
                :key="s.id || s.supplierId || s.supplierCode || s.supplierName"
                class="supplier-item"
                type="button"
                @click="selectSupplier(s)"
              >
                <div class="row-title">{{ s.supplierName || '-' }}</div>
                <div class="row-sub">
                  <span class="mono">{{ s.supplierCode || '-' }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="mini-btn" type="button" @click="changePage(-1)" :disabled="current <= 1 || loading">上一页</button>
          <span class="mini-page">{{ current }} / {{ totalPages }}</span>
          <button class="mini-btn" type="button" @click="changePage(1)" :disabled="current >= totalPages || loading">下一页</button>
        </div>
      </div>
    </div>

    <div v-if="confirmModalVisible" class="modal-overlay" @click.self="closeConfirmModal">
      <div class="confirm-modal">
        <div class="confirm-header">
          <div class="confirm-title">供应商编码不匹配</div>
        </div>
        <div class="confirm-body">
          <p class="confirm-text">当前选择的供应商：{{ selectedSupplier?.supplierName || '未知' }}</p>
          <p class="confirm-text">扫码的供应商编码：{{ parseScanText(mismatchScanText).supplierCode || '无' }}</p>
          <p class="confirm-text">供应商编码不匹配，无法扫码入库</p>
        </div>
        <div class="confirm-footer">
          <button class="mini-btn outline" type="button" @click="closeConfirmModal">取消</button>
          <button class="mini-btn" type="button" @click="switchSupplier">切换供应商</button>
        </div>
      </div>
    </div>

    <div v-if="wareModalVisible" class="modal-overlay" @click.self="closeWareModal">
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-title">选择仓库</div>
          <button class="modal-close" @click="closeWareModal" aria-label="关闭">×</button>
        </div>

        <div class="modal-body">
          <div v-if="wareLoading" class="loading-state">
            <div class="loading-spinner"></div>
            <p class="loading-text">加载中...</p>
          </div>
          <div v-else-if="wareList.length === 0" class="empty-state">
            <img src="@/assets/无数据.svg" alt="暂无数据" class="empty-img" />
            <p class="empty-text">~ 暂无仓库 ~</p>
          </div>
          <div v-else class="supplier-list">
            <button
              v-for="w in wareList"
              :key="w.id || w.wareNo || w.wareName"
              class="supplier-item"
              type="button"
              @click="selectWare(w)"
            >
              <div class="row-title">{{ w.wareName || '-' }}</div>
              <div class="row-sub">
                <span class="mono">{{ w.wareNo || '-' }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getSupplierPage, getMaterialPage, getWareList, scanSubmit } from '@/api/scanStockInNew'
import { getUicAuthUserInfo } from '@/utils/auth'

export default {
  name: 'ScanStockInNew',
  setup() {
    const router = useRouter()
    const route = useRoute()

    const scanInput = ref(null)
    const supplierKeywordInput = ref(null)

    const keyword = ref('')
    const current = ref(1)
    const size = ref(20)
    const total = ref(0)
    const list = ref([])
    const loading = ref(false)

    const supplierModalVisible = ref(false)
    const selectedSupplier = ref(null)
    const confirmModalVisible = ref(false)
    const mismatchScanText = ref('')
    
    const wareModalVisible = ref(false)
    const wareList = ref([])
    const selectedWare = ref(null)
    const wareLoading = ref(false)

    const bizDept = ref('')

    const scanText = ref('')
    const pendingScanText = ref('')
    const scannedList = ref([])

    const statusMsg = ref('')
    const statusType = ref('success')

    let scanTimer = 0
    let searchTimer = 0

    const totalPages = computed(() => {
      const t = total.value || 0
      const s = size.value || 20
      return Math.max(1, Math.ceil(t / s))
    })

    const setStatus = (msg, type = 'success') => {
      statusMsg.value = msg
      statusType.value = type
      if (msg) {
        window.setTimeout(() => {
          statusMsg.value = ''
        }, 2000)
      }
    }

    const normalizeSupplierRecords = (res) => {
      const d = res?.data
      if (d && Array.isArray(d.records)) return d.records
      if (d && d.data && Array.isArray(d.data.records)) return d.data.records
      if (Array.isArray(d)) return d
      if (Array.isArray(res?.records)) return res.records
      if (Array.isArray(res?.list)) return res.list
      return []
    }

    const normalizeTotal = (res) => {
      const d = res?.data
      if (typeof d?.total === 'number') return d.total
      if (typeof d?.data?.total === 'number') return d.data.total
      if (typeof res?.total === 'number') return res.total
      return 0
    }

    const fetchList = async () => {
      if (loading.value) return
      loading.value = true
      try {
        const res = await getSupplierPage({
          current: current.value,
          size: size.value,
          keyword: keyword.value
        })
        list.value = normalizeSupplierRecords(res)
        total.value = normalizeTotal(res)
      } catch (e) {
        list.value = []
        total.value = 0
        setStatus(e?.message || '加载失败', 'error')
      } finally {
        loading.value = false
      }
    }

    const handleSearchInput = () => {
      if (searchTimer) window.clearTimeout(searchTimer)
      searchTimer = window.setTimeout(async () => {
        current.value = 1
        await fetchList()
      }, 250)
    }

    const clearKeyword = async () => {
      keyword.value = ''
      current.value = 1
      await fetchList()
      await nextTick()
      supplierKeywordInput.value?.focus?.()
    }

    const changePage = async (delta) => {
      const next = current.value + delta
      if (next < 1) return
      if (next > totalPages.value) return
      current.value = next
      await fetchList()
    }

    const openSupplierModal = async () => {
      supplierModalVisible.value = true
      await nextTick()
      supplierKeywordInput.value?.focus?.()
      if (list.value.length === 0) {
        await fetchList()
      }
    }

    const closeSupplierModal = async () => {
      supplierModalVisible.value = false
      await nextTick()
      scanInput.value?.focus?.()
    }

    const buildPayload = (s) => {
      return {
        id: s?.id ?? s?.supplierId ?? '',
        supplierId: s?.supplierId ?? s?.id ?? '',
        supplierName: s?.supplierName ?? s?.name ?? '',
        supplierCode: s?.supplierCode ?? s?.code ?? '',
        supplierQrConfig: s?.supplierQrConfig ?? ''
      }
    }

    const tryParseJsonLoose = (s) => {
      if (!s) return null
      if (typeof s !== 'string') return null
      const trimmed = s.trim()
      if (!trimmed) return null
      try {
        return JSON.parse(trimmed)
      } catch (e) {
        try {
          const normalized = trimmed
            .replace(/\r?\n/g, ' ')
            .replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":')
          return JSON.parse(normalized)
        } catch (e2) {
          return null
        }
      }
    }

    const getQrConfig = () => {
      const raw = selectedSupplier.value?.supplierQrConfig
      const parsed = tryParseJsonLoose(raw)
      return {
        separator: parsed?.separator ?? '|',
        supplierCodeIndex: Number.isFinite(Number(parsed?.supplierCodeIndex)) ? Number(parsed.supplierCodeIndex) : 0,
        materialCodeIndex: Number.isFinite(Number(parsed?.materialCodeIndex)) ? Number(parsed.materialCodeIndex) : 
                          Number.isFinite(Number(parsed?.materialNodex)) ? Number(parsed.materialNodex) : 4,
        qtyIndex: Number.isFinite(Number(parsed?.qtyIndex)) ? Number(parsed.qtyIndex) : 
                 Number.isFinite(Number(parsed?.materialNumIndex)) ? Number(parsed.materialNumIndex) : 6,
        batchNoIndex: Number.isFinite(Number(parsed?.batchNoIndex)) ? Number(parsed.batchNoIndex) : -1
      }
    }

    const parseScanText = (text) => {
      const cfg = getQrConfig()
      const sep = String(cfg.separator || '|')
      const parts = String(text || '').split(sep)

      const supplierCode = parts?.[cfg.supplierCodeIndex] ?? ''
      const materialNo = parts?.[cfg.materialCodeIndex] ?? ''
      const qtyStr = parts?.[cfg.qtyIndex] ?? ''
      const batchNo = cfg.batchNoIndex >= 0 ? (parts?.[cfg.batchNoIndex] ?? '') : ''

      const qty = qtyStr === '' ? '' : Number(qtyStr)

      return {
        supplierCode: String(supplierCode || '').trim(),
        materialNo: String(materialNo || '').trim(),
        qty: Number.isFinite(qty) ? qty : String(qtyStr || '').trim(),
        batchNo: String(batchNo || '').trim()
      }
    }

    const validateSupplierCode = (scanSupplierCode) => {
      const expected = String(selectedSupplier.value?.supplierCode || '').trim()
      if (!expected) return true
      const actual = String(scanSupplierCode || '').trim()
      console.log('供应商编码校验:', { expected, actual, match: actual === expected })
      return actual === expected
    }

    const processScan = async (text) => {
      if (!text) return

      if (!selectedSupplier.value?.supplierId && !selectedSupplier.value?.id) {
        pendingScanText.value = text
        setStatus('请先选择供应商', 'warn')
        await openSupplierModal()
        return
      }

      if (!selectedWare.value?.id) {
        pendingScanText.value = text
        setStatus('请先选择仓库', 'warn')
        await openWareModal()
        return
      }

      const parsed = parseScanText(text)
      console.log('扫码解析结果:', parsed)
      console.log('当前供应商:', selectedSupplier.value)
      console.log('当前仓库:', selectedWare.value)
      
      if (!validateSupplierCode(parsed.supplierCode)) {
        mismatchScanText.value = text
        confirmModalVisible.value = true
        return
      }

      if (!parsed.materialNo || parsed.qty === '' || parsed.qty === null || typeof parsed.qty === 'undefined') {
        setStatus('扫码失败：二维码解析不完整', 'error')
        return
      }

      try {
        setStatus('正在获取物料信息...', 'warn')
        const supplierId = selectedSupplier.value?.supplierId || selectedSupplier.value?.id || ''
        const supplierName = selectedSupplier.value?.supplierName || ''
        
        const res = await getMaterialPage({
          materialNo: parsed.materialNo,
          type: 1,
          supplierId: supplierId,
          supplierName: supplierName,
          current: 1,
          size: 20,
          isCompose: 0
        })

        const records = res?.data?.records || res?.data?.data?.records || []
        const materialInfo = records.find(m => m.materialNo === parsed.materialNo)
        
        if (!materialInfo) {
          setStatus(`未找到物料：${parsed.materialNo}`, 'error')
          return
        }

        const incomingQty = Number(parsed.qty)
        const safeIncomingQty = Number.isFinite(incomingQty) ? incomingQty : 0

        scannedList.value.unshift({
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          ...parsed,
          qty: safeIncomingQty,
          materialId: materialInfo.id,
          materialDesc: materialInfo.materialDesc || '',
          materialModel: materialInfo.materialModel || '',
          packageUnitName: materialInfo.packageUnitName || ''
        })
        setStatus('扫码成功', 'success')
      } catch (e) {
        console.error('获取物料信息失败:', e)
        setStatus('获取物料信息失败', 'error')
      }
    }

    const handleScanInput = () => {
      if (scanTimer) window.clearTimeout(scanTimer)
      scanTimer = window.setTimeout(() => {
        if (scanText.value && scanText.value.length > 0) {
          handleScanEnter()
        }
      }, 150)
    }

    const handleScanEnter = async () => {
      const t = String(scanText.value || '').trim()
      scanText.value = ''
      await processScan(t)
      await nextTick()
      scanInput.value?.focus?.()
    }

    const clearScanText = async () => {
      scanText.value = ''
      await nextTick()
      scanInput.value?.focus?.()
    }

    const selectSupplier = async (s) => {
      selectedSupplier.value = buildPayload(s)
      supplierModalVisible.value = false
      setStatus(`已选择供应商：${selectedSupplier.value?.supplierName || ''}`, 'success')

      const pending = String(pendingScanText.value || '').trim()
      pendingScanText.value = ''
      await nextTick()
      scanInput.value?.focus?.()

      if (pending) {
        await processScan(pending)
      }
    }

    const closeConfirmModal = async () => {
      confirmModalVisible.value = false
      mismatchScanText.value = ''
      await nextTick()
      scanInput.value?.focus?.()
    }

    const switchSupplier = async () => {
      const text = mismatchScanText.value
      confirmModalVisible.value = false
      
      const parsed = parseScanText(text)
      const scanSupplierCode = parsed.supplierCode
      
      keyword.value = scanSupplierCode
      current.value = 1
      await fetchList()
      
      await nextTick()
      supplierModalVisible.value = true
    }

    const openWareModal = async () => {
      await fetchWareList()
      wareModalVisible.value = true
    }

    const closeWareModal = async () => {
      wareModalVisible.value = false
      await nextTick()
      scanInput.value?.focus?.()
    }

    const fetchWareList = async () => {
      if (!bizDept.value) {
        setStatus('未获取到项目组信息(bizDept)', 'warn')
        wareList.value = []
        return
      }
      try {
        wareLoading.value = true
        const res = await getWareList({ bizDept: bizDept.value, wareType: 0 })
        const data = res?.data || []
        wareList.value = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('获取仓库列表失败:', e)
        wareList.value = []
      } finally {
        wareLoading.value = false
      }
    }

    const selectWare = async (w) => {
      selectedWare.value = w
      wareModalVisible.value = false
      setStatus(`已选择仓库：${w.wareName || ''}`, 'success')
      await nextTick()
      scanInput.value?.focus?.()
    }

    const handleBack = () => {
      const back = route.query?.back
      if (back) {
        router.replace(String(back).startsWith('/') ? String(back) : `/${String(back)}`)
        return
      }
      router.back()
    }

    onMounted(async () => {
      keyword.value = ''
      const c = Number(route.query?.current)
      if (Number.isFinite(c) && c > 0) current.value = c

      const userInfo = getUicAuthUserInfo()
      bizDept.value = userInfo?.bizDept || userInfo?.deptId || '1457886524741734402'
      console.log('用户信息:', userInfo)
      console.log('获取的bizDept:', bizDept.value)

      await nextTick()
      scanInput.value?.focus?.()
    })

    onBeforeUnmount(() => {
      if (scanTimer) window.clearTimeout(scanTimer)
      if (searchTimer) window.clearTimeout(searchTimer)
    })

    const confirmStockIn = async () => {
      if (!selectedSupplier.value?.supplierId && !selectedSupplier.value?.id) {
        setStatus('请先选择供应商', 'warn')
        return
      }

      if (!selectedWare.value?.id) {
        setStatus('请先选择仓库', 'warn')
        return
      }

      if (scannedList.value.length === 0) {
        setStatus('请先扫码', 'warn')
        return
      }

      // 构建入库参数（提交时汇总；展示不合并）
      const cfg = getQrConfig()
      const hasBatch = Number(cfg?.batchNoIndex) >= 0

      const aggMap = new Map()
      scannedList.value.forEach(item => {
        const materialNo = String(item?.materialNo || '').trim()
        const batchNo = hasBatch ? String(item?.batchNo || '').trim() : ''
        const key = hasBatch ? `${materialNo}__${batchNo}` : materialNo
        const qty = Number(item?.qty)
        const safeQty = Number.isFinite(qty) ? qty : 0

        if (!aggMap.has(key)) {
          aggMap.set(key, {
            supplierId: selectedSupplier.value?.supplierId || selectedSupplier.value?.id || '',
            materialId: item?.materialId || '',
            materialNo,
            materialDesc: item?.materialDesc || '',
            materialModel: item?.materialModel || '',
            batchNo,
            packageUnitName: item?.packageUnitName || '',
            wareInNum: safeQty
          })
          return
        }

        const row = aggMap.get(key)
        row.wareInNum = (Number(row.wareInNum) || 0) + safeQty
      })

      const wareInDetailList = Array.from(aggMap.values())
      const wareInNum = wareInDetailList.reduce((sum, it) => sum + (Number(it.wareInNum) || 0), 0)

      const stockInParams = {
        supplierId: selectedSupplier.value?.supplierId || selectedSupplier.value?.id || '',
        supplierName: selectedSupplier.value?.supplierName || '',
        supplierCode: selectedSupplier.value?.supplierCode || '',
        wareInType: 0,
        type: 0,
        wareInNum,
        wareMaterialNum: wareInDetailList.length,
        wareInDetailList,
        // 仓库信息
        storeId: selectedWare.value?.id || '',
        storeName: selectedWare.value?.wareName || ''
      }

      console.log('入库参数:', stockInParams)
      if (loading.value) return
      loading.value = true
      try {
        setStatus('提交入库中...', 'warn')
        const res = await scanSubmit(stockInParams)
        if (res?.success === false) {
          setStatus(res?.msg || '入库失败', 'error')
          return
        }
        setStatus('入库成功', 'success')
        scannedList.value = []
      } catch (e) {
        console.error('入库失败:', e)
        setStatus(e?.message || '入库失败', 'error')
      } finally {
        loading.value = false
      }
    }

    return {
      scanInput,
      supplierKeywordInput,
      keyword,
      current,
      total,
      totalPages,
      list,
      loading,
      supplierModalVisible,
      selectedSupplier,
      confirmModalVisible,
      mismatchScanText,
      scanText,
      scannedList,
      statusMsg,
      statusType,
      parseScanText,
      handleBack,
      openSupplierModal,
      closeSupplierModal,
      closeConfirmModal,
      switchSupplier,
      handleSearchInput,
      handleScanInput,
      clearKeyword,
      changePage,
      handleScanEnter,
      clearScanText,
      selectSupplier,
      confirmStockIn,
      // 仓库相关
      wareModalVisible,
      selectedWare,
      wareList,
      wareLoading,
      openWareModal,
      closeWareModal,
      selectWare
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.header-right {
  width: 40px;
  height: 40px;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.content {
  padding: 6px 8px;
  margin-top: calc(64px + env(safe-area-inset-top));
  padding-bottom: calc(86px + env(safe-area-inset-bottom));
  overflow: auto;
}

.scan-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.supplier-bar {
  background: #fff;
  border: 1px solid rgba(2, 8, 23, 0.06);
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.supplier-left {
  min-width: 0;
}

.supplier-label {
  font-size: 12px;
  color: #666;
}

.supplier-value {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scan-input-wrap {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.scan-input::placeholder {
  color: #999;
  font-size: 15px;
}

.scan-input {
  width: 100%;
  height: 44px;
  border: 2px solid rgba(2, 8, 23, 0.08);
  border-radius: 12px;
  padding: 0 40px 0 14px;
  outline: none;
  font-size: 16px;
  background: #fafbfc;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
}

.scan-input:focus {
  border-color: #23b1c0;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(35, 177, 192, 0.12);
}

.status-banner {
  padding: 10px 12px;
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  border: 1px solid rgba(2, 8, 23, 0.06);
}

.status-banner.success {
  background: #e6fffb;
  color: #08979c;
}

.status-banner.warn {
  background: #fffbe6;
  color: #d48806;
}

.status-banner.error {
  background: #fff1f0;
  color: #cf1322;
}

.search-bar {
  position: relative;
  background: #fff;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #eee;
}

.search-input {
  width: 100%;
  height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 36px 0 10px;
  outline: none;
  box-sizing: border-box;
}

.input-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: #e5e7eb;
  font-size: 16px;
  line-height: 28px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.input-clear:hover {
  background: #d1d5db;
  color: #374151;
}

.input-clear:active {
  background: #9ca3af;
  color: #fff;
}

.loading-state {
  padding: 40px 0;
  text-align: center;
}

.loading-spinner {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: #1677ff;
  margin: 0 auto 10px;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  padding: 18px 0 8px;
  text-align: center;
}

.empty-img {
  width: 92px;
  opacity: 0.6;
}

.empty-text {
  color: #888;
  margin-top: 8px;
  font-size: 13px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.data-card {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(2, 8, 23, 0.06);
  background: #fff;
  border-radius: 14px;
  padding: 12px 12px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

.row-title {
  font-size: 14px;
  font-weight: 600;
  color: #111;
}

.row-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.split {
  margin: 0 6px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 16px;
}

.modal-container {
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.2);
  box-sizing: border-box;
}

.modal-header {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.modal-title {
  font-size: 17px;
  font-weight: 600;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 32px;
  color: #64748b;
}

.modal-body {
  padding: 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.modal-scan-input-wrap {
  position: relative;
  background: #fff;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(2, 8, 23, 0.06);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.modal-search {
  position: relative;
  background: #fff;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid rgba(2, 8, 23, 0.06);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  box-sizing: border-box;
  width: 100%;
}

.dropdown-empty {
  padding: 18px 0;
  text-align: center;
  color: #888;
  font-size: 13px;
}

.modal-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.supplier-item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(2, 8, 23, 0.06);
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
}

.modal-footer {
  border-top: 1px solid #eee;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.mini-page {
  font-size: 12px;
  color: #666;
}

.mini-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 14px;
  background: #23b1c0;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
}

.mini-btn.outline {
  flex: 0 0 auto;
  padding: 0 16px;
  background: #fff;
  color: #23b1c0;
  border: 1px solid rgba(35, 177, 192, 0.8);
  font-size: 14px;
  font-weight: 500;
}

.mini-btn:disabled {
  background: rgba(35, 177, 192, 0.35);
}

.confirm-modal {
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.2);
}

.confirm-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: #111;
}

.confirm-body {
  padding: 20px 16px;
}

.confirm-text {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.confirm-text:last-child {
  margin-bottom: 0;
}

.confirm-footer {
  padding: 12px 16px;
  display: flex;
  gap: 12px;
  border-top: 1px solid #eee;
}

.confirm-footer .mini-btn {
  flex: 1;
}

.submit-section {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #eee;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #111;
}

.submit-btn {
  min-width: 120px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: #23b1c0;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-btn:hover {
  background: #1a94a0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(35, 177, 192, 0.3);
}

.submit-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(35, 177, 192, 0.3);
}
</style>
